import { chain, clone } from 'lodash'
import { ApiCaller, callApi, getUrlWithPathParams } from 'services/api/utils'
import { getFormFormattedDate, normalizeDataForApi } from 'utils'

import { Education, newPerson, Person, PersonData, Position } from './types'

const ADMIN_PEOPLE_ENDPOINT_PATH = '/admin/people/'
const POSITION_ENDPOINT_PATH = '/positions/'
const EDUCATION_ENDPOINT_PATH = '/educations/'

const apiGetPerson = (apiCaller: ApiCaller) => async (id: number): Promise<ApiPerson> => {
  const personData = await apiCaller<ApiPerson>({
    url: getUrlWithPathParams(ADMIN_PEOPLE_ENDPOINT_PATH, id),
  })
  return normalizeApiPersonData(personData)
}

export const apiGetPersonAsCurrentUser = apiGetPerson(callApi)
export const apiGetPersonAsAdmin = apiGetPerson(callApi)

const apiGetPeople = (apiCaller: ApiCaller) => async (
  currentPage: number = 0,
  limit?: number,
  search?: string
): Promise<ApiIdIndexedPeople> => {
  const peopleData = await apiCaller<ApiPerson[]>({
    url: ADMIN_PEOPLE_ENDPOINT_PATH,
    params: {
      skip: limit ? currentPage * limit : 0,
      limit,
      search,
      include: ['currentPositions'],
    },
  })
  return chain(peopleData).map(normalizeApiPersonData).keyBy('id').value()
}

export const apiGetCurrentUserPeople = apiGetPeople(callApi)
export const apiGetAllPeople = apiGetPeople(callApi)

const apiPutPerson = (apiCaller: ApiCaller) => async (person: ApiPerson): Promise<ApiPerson> => {
  return await apiCaller<ApiPerson>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_PEOPLE_ENDPOINT_PATH, person.id),
    data: normalizeDataForApi(person),
  })
}

export const apiPutPersonAsCurrentUser = apiPutPerson(callApi)
export const apiPutPersonAsAdmin = apiPutPerson(callApi)

export async function apiPostPerson(personData: PersonData): Promise<ApiPerson> {
  return await callApi<ApiPerson>({
    method: 'post',
    url: ADMIN_PEOPLE_ENDPOINT_PATH,
    data: normalizeDataForApi(personData),
  })
}

const apiGetPersonEducations = (apiCaller: ApiCaller) => async (personId: number): Promise<Education[]> => {
  const educations = await apiCaller<ApiEducation[]>({
    method: 'get',
    url: EDUCATION_ENDPOINT_PATH,
    params: {
      personId,
      include: ['organization'],
    },
  })

  return educations.map(normalizeApiEducationData)
}

export const apiGetPersonEducationsAsCurrentUser = apiGetPersonEducations(callApi)
export const apiGetPersonEducationsAsAdmin = apiGetPersonEducations(callApi)

const apiPutEducation = (apiCaller: ApiCaller) => async (
  education: Education,
  personId: number
): Promise<Education> => {
  const updatedEducation = await apiCaller<ApiEducation>({
    method: 'put',
    url: getUrlWithPathParams(EDUCATION_ENDPOINT_PATH, education.id),
    data: {
      ...normalizeDataForApi(education),
      personId,
    },
  })
  return normalizeApiEducationData(updatedEducation)
}

export const apiPutEducationAsCurrentUser = apiPutEducation(callApi)
export const apiPutEducationAsAdmin = apiPutEducation(callApi)

const apiDeleteEducation = (apiCaller: ApiCaller) => async (education: Education): Promise<boolean> => {
  await apiCaller<boolean>({
    method: 'delete',
    url: getUrlWithPathParams(EDUCATION_ENDPOINT_PATH, education.id),
  })
  return true
}

export const apiDeleteEducationAsCurrentUser = apiDeleteEducation(callApi)
export const apiDeleteEducationAsAdmin = apiDeleteEducation(callApi)

const apiPostEducation = (apiCaller: ApiCaller) => async (
  education: Education,
  personId: number
): Promise<Education> => {
  const updatedEducation = await apiCaller<ApiEducation>({
    method: 'post',
    url: EDUCATION_ENDPOINT_PATH,
    data: {
      ...normalizeDataForApi(education),
      personId,
    },
  })
  return normalizeApiEducationData(updatedEducation)
}

export const apiPostEducationAsCurrentUser = apiPostEducation(callApi)
export const apiPostEducationAsAdmin = apiPostEducation(callApi)

const apiGetPersonRoles = (apiCaller: ApiCaller) => async (personId: number): Promise<Position[]> => {
  const roles = await apiCaller<ApiPosition[]>({
    method: 'get',
    url: POSITION_ENDPOINT_PATH,
    params: {
      personId,
      include: ['organization'],
    },
  })

  return roles.map(normalizeApiPositionData)
}

export const apiGetPersonRolesAsCurrentUser = apiGetPersonRoles(callApi)
export const apiGetPersonRolesAsAdmin = apiGetPersonRoles(callApi)

const apiPutPosition = (apiCaller: ApiCaller) => async (position: Position, personId: number): Promise<Position> => {
  const updatedPosition = await apiCaller<ApiPosition>({
    method: 'put',
    url: getUrlWithPathParams(POSITION_ENDPOINT_PATH, position.id),
    data: {
      ...normalizeDataForApi(position),
      personId,
    },
  })
  return normalizeApiPositionData(updatedPosition)
}

export const apiPutPositionAsCurrentUser = apiPutPosition(callApi)
export const apiPutPositionAsAdmin = apiPutPosition(callApi)

const apiDeletePosition = (apiCaller: ApiCaller) => async (position: Position): Promise<boolean> => {
  await apiCaller<boolean>({
    method: 'delete',
    url: getUrlWithPathParams(POSITION_ENDPOINT_PATH, position.id),
  })
  return true
}

export const apiDeletePositionAsCurrentUser = apiDeletePosition(callApi)
export const apiDeletePositionAsAdmin = apiDeletePosition(callApi)

const apiPostPosition = (apiCaller: ApiCaller) => async (position: Position, personId: number): Promise<Position> => {
  const updatedPosition = await apiCaller<ApiPosition>({
    method: 'post',
    url: POSITION_ENDPOINT_PATH,
    data: {
      ...normalizeDataForApi(position),
      personId,
    },
  })
  return normalizeApiPositionData(updatedPosition)
}

export const apiPostPositionAsCurrentUser = apiPostPosition(callApi)
export const apiPostPositionAsAdmin = apiPostPosition(callApi)

function normalizeApiPersonData(person: ApiPerson): Person {
  const normalizedPerson = clone({ ...newPerson(), ...person })
  normalizedPerson.bornOn = normalizedPerson.bornOn
    ? getFormFormattedDate(new Date(normalizedPerson.bornOn))
    : undefined
  normalizedPerson.diedOn = normalizedPerson.diedOn
    ? getFormFormattedDate(new Date(normalizedPerson.diedOn))
    : undefined
  return normalizedPerson as Person
}

function normalizeApiPositionData(position: ApiPosition): Position {
  const { startedOn, endedOn, ...positionData } = position
  return {
    ...positionData,
    startedOn: startedOn ? getFormFormattedDate(new Date(startedOn)) : '',
    endedOn: endedOn ? getFormFormattedDate(new Date(endedOn)) : '',
  } as Position
}

function normalizeApiEducationData(education: ApiEducation): Education {
  const { startedOn, endedOn, ...educationData } = education
  return {
    ...educationData,
    startedOn: startedOn ? getFormFormattedDate(new Date(startedOn)) : '',
    endedOn: endedOn ? getFormFormattedDate(new Date(endedOn)) : '',
  } as Education
}

type ApiIdIndexedPeople = {
  [personId: number]: Person
}

type ApiPerson = Partial<Omit<Person, 'id'>> & Pick<Person, 'id'>
type ApiPosition = Partial<Omit<Position, 'id'>> & Pick<Position, 'id'>
type ApiEducation = Partial<Omit<Education, 'id'>> & Pick<Education, 'id'>
